import React from 'react';

function formatDistance(distanceMeters) {
  if (!Number.isFinite(distanceMeters)) {
    return 'Entfernung unbekannt';
  }

  return `${Math.round(distanceMeters)} m entfernt`;
}

function formatBottleExpiry(value) {
  if (!value) {
    return '';
  }

  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function parseDateMs(value) {
  if (!value) {
    return null;
  }

  const date = new Date(String(value).replace(' ', 'T'));
  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatRelativeDateTime(value) {
  const timestamp = parseDateMs(value);
  if (!timestamp) {
    return '';
  }

  const diffMs = Date.now() - timestamp;
  if (diffMs < 0) {
    return '';
  }

  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) {
    return 'gerade eben';
  }

  if (diffMinutes < 60) {
    return diffMinutes === 1 ? 'vor 1 Minute' : `vor ${diffMinutes} Minuten`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return diffHours === 1 ? 'vor 1 Stunde' : `vor ${diffHours} Stunden`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? 'vor 1 Tag' : `vor ${diffDays} Tagen`;
}

function formatConfirmationCount(value) {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) {
    return 'Noch nicht bestätigt';
  }

  return count === 1 ? '1 Bestätigung' : `${count} Bestätigungen`;
}

function buildItemKey(item) {
  return `${item?.sourceType || 'item'}-${item?.id ?? 'unknown'}`;
}

function isGiveawayDonation(item) {
  return item?.sourceType === 'donation' && (item?.item_type === 'giveaway' || item?.pfand_type === 'zu_verschenken');
}

function ItemCard({ item, selectedItemKey, onSelectItem }) {
  const isSelected = selectedItemKey === buildItemKey(item);

  return (
    <article
      className={`treasure-map-list__card ${isSelected ? 'is-selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelectItem?.(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectItem?.(item);
        }
      }}
    >
      <div className="treasure-map-list__card-head">
        <div>
          <h4>{item.title || item.name || 'Unbenannter Punkt'}</h4>
          <div className="treasure-map-list__meta-row">
            <span>{item.categoryLabel || item.category || 'Unbekannt'}</span>
            <span>{formatDistance(item.distanceMeters)}</span>
          </div>
        </div>
        <span className={`treasure-map-list__badge is-${item.sourceType || 'location'}`}>
          {item.sourceType === 'donation'
            ? (isGiveawayDonation(item) ? 'Zu verschenken' : 'Pfandspende')
            : item.sourceType === 'bottle_message'
              ? 'Flaschenpost'
              : 'Ort'}
        </span>
      </div>

      {item.description && <p>{item.description}</p>}

      {item.sourceType === 'donation' && (
        <div className="treasure-map-list__confirmation-row">
          <span className={`treasure-map-list__freshness-pill ${item.isOlderThan24h ? 'is-stale' : ''}`.trim()}>
            Gemeldet {formatRelativeDateTime(item.created_at || item.date) || 'unbekannt'}
          </span>
          <span className="treasure-map-list__confirmation-pill">
            👍 {formatConfirmationCount(item.confirmation_count)}
          </span>
        </div>
      )}

      {item.sourceType === 'bottle_message' && item.expires_at && (
        <div className="treasure-map-list__meta-row">
          <span>Aktiv bis {formatBottleExpiry(item.expires_at)}</span>
        </div>
      )}

      <div className="treasure-map-list__select-row">
        <button
          type="button"
          className="treasure-map-list__select-button"
          onClick={(event) => {
            event.stopPropagation();
            onSelectItem?.(item);
          }}
        >
          {isSelected ? 'Ausgewählt' : 'Details anzeigen'}
        </button>
      </div>
    </article>
  );
}

export function TreasureMapListView({ items = [], selectedItemKey, onSelectItem }) {
  const safeItems = Array.isArray(items) ? items : [];

  if (!safeItems.length) {
    return (
      <div className="treasure-map-list treasure-map-list--empty">
        Keine Punkte für die aktuelle Filterauswahl gefunden.
      </div>
    );
  }

  return (
    <div className="treasure-map-list">
      {safeItems.map((item) => (
        <ItemCard
          key={`${item.sourceType || 'item'}-${item.id}`}
          item={item}
          selectedItemKey={selectedItemKey}
          onSelectItem={onSelectItem}
        />
      ))}
    </div>
  );
}

export default TreasureMapListView;
